import React, { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import Layout from "@components/Layout";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import WeightTracking from "./WeightTracking";
import Dashboard from "./Dashboard";
import FieldTrials from "./FieldTrials";

// ── Tab bar ─────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #f0f2f3;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 24px;
  font-family: Nunito Sans, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ active }) => (active ? "#68ad18" : "#899093")};
  background: none;
  border: none;
  border-bottom: 2.5px solid ${({ active }) => (active ? "#68ad18" : "transparent")};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: ${({ active }) => (active ? "#68ad18" : "#494c5b")};
  }
`;

type AnalyticsTab = "weight" | "dashboard" | "trials";

const AnalyticsScreen: React.FC = () => {
  const lang = useLang();
  const { accountStore } = useStores();
  // Trials are per field — hidden for a team role without fields & yield maps.
  const canFields = accountStore.canViewFields;

  const [activeTab, setActiveTab] = useState<AnalyticsTab>("weight");

  const weightTabLabel = lang.analytics?.weightTrackingTitle ?? "Weight Tracking";
  const dashboardTabLabel = lang.analytics?.dashboardTab ?? "Dashboard";
  const trialsTabLabel = lang.analytics?.trialsTab ?? "Trials";

  return (
    <Layout title={lang.analytics?.screenTitle ?? "Analytics"}>
      <TabBar>
        <Tab
          active={activeTab === "weight"}
          onClick={() => setActiveTab("weight")}
        >
          {weightTabLabel}
        </Tab>
        <Tab
          active={activeTab === "dashboard"}
          onClick={() => setActiveTab("dashboard")}
        >
          {dashboardTabLabel}
        </Tab>
        {canFields && (
          <Tab
            active={activeTab === "trials"}
            onClick={() => setActiveTab("trials")}
          >
            {trialsTabLabel}
          </Tab>
        )}
      </TabBar>

      {activeTab === "dashboard" && <Dashboard />}

      {activeTab === "trials" && canFields && <FieldTrials />}

      {activeTab === "weight" && <WeightTracking />}
    </Layout>
  );
};

export default observer(AnalyticsScreen);
